import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, Circle, Package, AlertCircle } from 'lucide-react';
import axios from 'axios';

/**
 * Modal for bulk receiving packages
 * Shows list of all driver's delivery points with checkboxes
 * Sorted by sequence DESCENDING (highest first) - driver receives last package first
 * Allows driver to mark multiple packages as received and submit all at once
 */
function BulkReceiveModal({ isOpen, onClose, driverId, deliveries, onBulkReceived }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  // Filter only active (non-submitted) deliveries and sort by sequence DESCENDING
  const activeDeliveries = deliveries
    .filter(d => !d.is_submitted)
    .sort((a, b) => b.sequence - a.sequence); // Reverse sort: highest sequence first

  // Auto-hide message
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds(new Set());
      setMessage(null);
    }
  }, [isOpen]);

  /**
   * Toggle delivery point selection
   */
  const toggleSelection = (deliveryId) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deliveryId)) {
        newSet.delete(deliveryId);
      } else {
        newSet.add(deliveryId);
      }
      return newSet;
    });
  };

  /**
   * Select/Deselect all
   */
  const toggleSelectAll = () => {
    if (selectedIds.size === activeDeliveries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(activeDeliveries.map(d => d.id)));
    }
  };

  /**
   * Submit bulk receive
   */
  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      setMessage({ type: 'error', text: 'Seleccione al menos un paquete' });
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/packages/bulk-receive`,
        {
          delivery_point_ids: Array.from(selectedIds),
          driver_id: driverId,
          new_status: 'with_driver'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const { success_count, failed_count } = response.data;

      setMessage({
        type: 'success',
        text: `✓ ${success_count} paquete(s) recibido(s)${failed_count > 0 ? `, ${failed_count} fallaron` : ''}`
      });

      // Notify parent to refresh
      onBulkReceived({
        updated_delivery_points: Array.from(selectedIds),
        success_count,
        failed_count
      });

      // Auto-close after success
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error bulk receiving:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Error al recibir paquetes'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-montserrat font-bold text-gray-800">
              Recibir Paquetes
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {selectedIds.size} de {activeDeliveries.length} seleccionados
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Ordenado: Último paquete arriba (seq. {activeDeliveries[0]?.sequence} → {activeDeliveries[activeDeliveries.length - 1]?.sequence})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Message Banner */}
        {message && (
          <div className={`mx-4 mt-4 p-3 rounded-lg border-l-4 flex-shrink-0 ${
            message.type === 'success'
              ? 'bg-green-50 border-brand-green text-brand-green'
              : 'bg-red-50 border-red-500 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <p className="font-montserrat font-semibold text-sm">
                {message.text}
              </p>
            </div>
          </div>
        )}

        {/* Select All Button */}
        <div className="px-4 pt-4 flex-shrink-0">
          <button
            onClick={toggleSelectAll}
            disabled={submitting}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-montserrat font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {selectedIds.size === activeDeliveries.length ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-brand-primary" />
                <span>Deseleccionar Todos</span>
              </>
            ) : (
              <>
                <Circle className="w-5 h-5" />
                <span>Seleccionar Todos</span>
              </>
            )}
          </button>
        </div>

        {/* Scrollable List */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {activeDeliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="font-montserrat">No hay entregas activas</p>
            </div>
          ) : (
            <div className="space-y-2">
              {activeDeliveries.map((delivery, index) => {
                const isSelected = selectedIds.has(delivery.id);
                const isFirst = index === 0;
                const isLast = index === activeDeliveries.length - 1;
                
                return (
                  <button
                    key={delivery.id}
                    onClick={() => toggleSelection(delivery.id)}
                    disabled={submitting}
                    className={`
                      w-full text-left p-4 rounded-lg border-2 transition-all relative
                      ${isSelected
                        ? 'border-brand-primary bg-brand-primary/5'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                      }
                      ${submitting ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {/* Position badges */}
                    {isFirst && (
                      <div className="absolute top-2 right-2">
                        <span className="text-xs font-bold bg-brand-gold text-white px-2 py-0.5 rounded">
                          Último en recibir
                        </span>
                      </div>
                    )}
                    {isLast && (
                      <div className="absolute top-2 right-2">
                        <span className="text-xs font-bold bg-brand-green text-white px-2 py-0.5 rounded">
                          Primero en recibir
                        </span>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-1">
                        {isSelected ? (
                          <CheckCircle2 className="w-6 h-6 text-brand-primary" />
                        ) : (
                          <Circle className="w-6 h-6 text-gray-400" />
                        )}
                      </div>

                      {/* Delivery Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`
                            w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold
                            ${isSelected ? 'bg-brand-primary text-white' : 'bg-gray-200 text-gray-600'}
                          `}>
                            {delivery.sequence}
                          </span>
                          <span className="font-semibold text-gray-900 truncate">
                            {delivery.recipient_name}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 truncate">
                          {delivery.product}
                        </p>
                        
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-gray-500">
                            ID: {delivery.id}
                          </span>
                          {delivery.tracking_code && (
                            <span className="text-xs text-gray-500">
                              • {delivery.tracking_code}
                            </span>
                          )}
                          {delivery.package_status && (
                            <span className={`
                              text-xs px-1.5 py-0.5 rounded
                              ${delivery.package_status === 'with_driver'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                              }
                            `}>
                              {delivery.package_status === 'with_driver' ? '✓ Recibido' : delivery.package_status}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Submit Button */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={handleSubmit}
            disabled={selectedIds.size === 0 || submitting}
            className={`
              w-full font-montserrat font-semibold py-4 px-6 rounded-lg transition-all
              ${selectedIds.size > 0 && !submitting
                ? 'bg-brand-primary hover:bg-brand-primary/90 text-white'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {submitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Procesando...</span>
              </div>
            ) : (
              `Recibir ${selectedIds.size} Paquete${selectedIds.size !== 1 ? 's' : ''}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkReceiveModal;